import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paperclip, Upload, Download, Trash2, Loader, FileText, FileSpreadsheet, Image as ImageIcon, File, Sparkles, Lock, Eye } from 'lucide-react';
import { piecesJointesApi } from '../utils/api';
import DocViewerModal from './DocViewerModal';

// Formats visualisables directement dans le navigateur (sans téléchargement).
const VIEWABLE = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'svg', 'txt', 'csv'];
const isViewable = (nom) => VIEWABLE.includes((nom || '').split('.').pop().toLowerCase());

const ACCEPT = '.pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.xlsm,.csv,.ods,.ppt,.pptx,.odp,.png,.jpg,.jpeg,.gif,.webp,.heic';

// Catégories de la bibliothèque de documents (ordre d'affichage)
export const DOC_CATEGORIES = [
  { key: 'sante', label: 'Santé', badge: 'bg-rose-100 text-rose-700', team: true },
  { key: 'contrat', label: 'Contrat', badge: 'bg-navy-100 text-navy-700' },
  { key: 'devis', label: 'Devis', badge: 'bg-gold-100 text-gold-700' },
  { key: 'administratif', label: 'Administratif', badge: 'bg-slate-100 text-slate-700' },
  { key: 'autre', label: 'Autre', badge: 'bg-cream-200 text-navy-500' },
];
const catMeta = (k) => DOC_CATEGORIES.find(c => c.key === k) || { key: k || 'autre', label: k || 'Autre', badge: 'bg-cream-200 text-navy-500' };

function fmtSize(o) {
  if (!o && o !== 0) return '';
  if (o < 1024) return o + ' o';
  if (o < 1024 * 1024) return Math.round(o / 1024) + ' Ko';
  return (o / (1024 * 1024)).toFixed(1) + ' Mo';
}

function iconFor(nom) {
  const ext = (nom || '').split('.').pop().toLowerCase();
  if (['xls', 'xlsx', 'xlsm', 'csv', 'ods'].includes(ext)) return { Icon: FileSpreadsheet, c: 'text-emerald-600' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext)) return { Icon: ImageIcon, c: 'text-violet-500' };
  if (['pdf'].includes(ext)) return { Icon: FileText, c: 'text-red-500' };
  if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)) return { Icon: FileText, c: 'text-blue-600' };
  return { Icon: File, c: 'text-navy-400' };
}

const EXTRACTABLE = ['pdf', 'doc', 'docx', 'txt', 'rtf'];

/**
 * Bibliothèque de pièces jointes réutilisable.
 * @param {string} entiteType  ex: 'enfant', 'employe', 'contrat', 'facture', 'structure', 'protocole', 'demande'
 * @param {number|null} entiteId
 * @param {string} [title]
 * @param {function} [onExtract]  action « pré-remplir » (IA) sur les documents extractibles
 * @param {boolean} [library]  active le mode bibliothèque (classement par catégorie)
 */
export default function PiecesJointes({ entiteType, entiteId = null, title = 'Pièces jointes', onExtract = null, library = false }) {
  const [items, setItems] = useState([]);
  const [canManage, setCanManage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [extractingId, setExtractingId] = useState(null);
  const [uploadCat, setUploadCat] = useState('sante');
  const [dragOver, setDragOver] = useState(false);
  const [viewing, setViewing] = useState(null);
  const inputRef = useRef(null);

  const doExtract = async (pj) => {
    if (!onExtract) return;
    setExtractingId(pj.id);
    try { await onExtract(pj); } finally { setExtractingId(null); }
  };

  const load = useCallback(() => {
    if (!entiteType) return;
    setLoading(true);
    piecesJointesApi.list(entiteType, entiteId)
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data.rows || []);
        setItems(rows);
        setCanManage(Array.isArray(data) ? true : !!data.can_manage);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [entiteType, entiteId]);
  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true); setError(null);
    for (const f of files) {
      const fd = new FormData();
      fd.append('fichier', f);
      fd.append('entite_type', entiteType);
      if (entiteId != null) fd.append('entite_id', entiteId);
      if (library) fd.append('categorie', canManage ? uploadCat : 'sante');
      try { await piecesJointesApi.upload(fd); }
      catch (err) { setError(err.response?.data?.error || `Échec de l'envoi de « ${f.name} »`); }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    load();
  };

  const remove = async (id, nom) => {
    if (!window.confirm(`Supprimer « ${nom} » ?`)) return;
    try { await piecesJointesApi.delete(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const changeCat = async (id, categorie) => {
    try { await piecesJointesApi.setCategorie(id, categorie); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const catList = canManage ? DOC_CATEGORIES : DOC_CATEGORIES.filter(c => c.team);

  const renderRow = (pj) => {
    const { Icon, c } = iconFor(pj.nom_original);
    const meta = catMeta(pj.categorie);
    return (
      <div key={pj.id} className="px-4 py-2.5 flex items-center gap-3">
        <Icon size={18} className={`${c} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-navy-800 truncate flex items-center gap-2">
            {pj.nom_original}
            {library && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.badge}`}>{meta.label}</span>}
          </div>
          <div className="text-[11px] text-navy-300">{fmtSize(pj.taille)}{pj.created_at ? ` · ${String(pj.created_at).slice(0, 10)}` : ''}</div>
        </div>
        {library && canManage && (
          <select value={pj.categorie || 'autre'} onChange={e => changeCat(pj.id, e.target.value)} title="Changer l'affectation"
            className="text-[12px] border border-cream-300 rounded-lg px-1.5 py-1 bg-white text-navy-600 hidden sm:block">
            {DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}
        {onExtract && canManage && EXTRACTABLE.includes((pj.nom_original || '').split('.').pop().toLowerCase()) && (
          <button onClick={() => doExtract(pj)} disabled={extractingId === pj.id}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-gold-700 hover:bg-gold-50 rounded-lg px-2 py-1.5 disabled:opacity-50" title="Extraire les infos vers le dossier">
            {extractingId === pj.id ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />} Pré-remplir
          </button>
        )}
        {isViewable(pj.nom_original) && (
          <button type="button" onClick={() => setViewing(pj)}
            className="p-2 rounded-lg text-navy-300 hover:bg-navy-50 hover:text-navy-700" title="Visualiser sans télécharger">
            <Eye size={16} />
          </button>
        )}
        <a href={piecesJointesApi.downloadUrl(pj.id)} target="_blank" rel="noopener noreferrer"
          className="p-2 rounded-lg text-navy-300 hover:bg-navy-50 hover:text-navy-700" title="Télécharger">
          <Download size={16} />
        </a>
        {canManage && (
          <button onClick={() => remove(pj.id, pj.nom_original)}
            className="p-2 rounded-lg text-navy-200 hover:bg-red-50 hover:text-red-500" title="Supprimer">
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  };

  // Regroupement par catégorie (mode bibliothèque)
  const groups = library
    ? catList.map(cat => ({ cat, rows: items.filter(i => (i.categorie || 'autre') === cat.key) })).filter(g => g.rows.length > 0)
    : null;

  const onDragOver = (e) => { e.preventDefault(); if (!uploading && !dragOver) setDragOver(true); };
  const onDragLeave = (e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length) handleFiles(files);
  };

  return (
    <div className="relative bg-white rounded-xl border border-cream-300/70 overflow-hidden"
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {dragOver && (
        <div className="absolute inset-0 z-20 bg-navy-700/[0.06] border-2 border-dashed border-navy-400 rounded-xl flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 text-navy-700 text-sm font-semibold bg-white/90 px-4 py-2 rounded-full shadow-sm">
            <Upload size={18} className="text-gold-600" /> Déposez vos documents ici
          </div>
        </div>
      )}
      <div className="px-4 py-3 bg-cream-50 border-b border-cream-200 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-navy-700 text-sm flex items-center gap-2">
          <Paperclip size={15} className="text-gold-600" /> {title}
          {items.length > 0 && <span className="text-[11px] font-medium text-navy-300">({items.length})</span>}
        </h3>
        <div className="flex items-center gap-2">
          {library && canManage && (
            <select value={uploadCat} onChange={e => setUploadCat(e.target.value)}
              className="text-[12.5px] border border-cream-300 rounded-lg px-2 py-1.5 bg-white text-navy-600" title="Type de document à ajouter">
              {DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          )}
          <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={uploading}
            title="Ajouter un document — ou glissez-déposez vos fichiers directement dans le cadre"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-navy-700 hover:bg-navy-800 rounded-lg px-3 py-1.5 disabled:opacity-50">
            {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />} {library ? 'Ajouter un document' : 'Ajouter'}
          </button>
        </div>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
          onChange={(e) => handleFiles(e.target.files)} />
      </div>

      {library && !canManage && (
        <div className="px-4 py-2 text-[12px] text-navy-400 bg-cream-50/60 border-b border-cream-200 flex items-center gap-1.5">
          <Lock size={12} /> Vous voyez uniquement les documents de <strong>santé</strong>.
        </div>
      )}

      {error && <div className="px-4 py-2 text-[13px] text-red-600 bg-red-50 border-b border-red-100">{error}</div>}

      {loading ? (
        <div className="py-6 text-center text-navy-300 text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <button type="button" onClick={() => inputRef.current && inputRef.current.click()}
          className="w-full py-8 text-center text-navy-300 text-[13px] hover:bg-cream-50 transition-colors">
          Aucun document.<br />
          <span className="text-navy-400 underline">Glissez-déposez</span> vos fichiers ici, ou <span className="text-navy-400 underline">cliquez pour ajouter</span><br />
          <span className="text-[11.5px] text-navy-200">Word, PDF, Excel, image…</span>
        </button>
      ) : library ? (
        <div>
          {groups.map(({ cat, rows }) => (
            <div key={cat.key}>
              <div className="px-4 py-1.5 bg-cream-50/70 border-b border-cream-200 flex items-center gap-2">
                <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full font-semibold ${cat.badge}`}>{cat.label}</span>
                <span className="text-[11px] text-navy-300">{rows.length}</span>
              </div>
              <div className="divide-y divide-cream-200">{rows.map(renderRow)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-cream-200">{items.map(renderRow)}</div>
      )}

      {viewing && (
        <DocViewerModal
          url={piecesJointesApi.viewUrl(viewing.id)}
          downloadUrl={piecesJointesApi.downloadUrl(viewing.id)}
          filename={viewing.nom_original}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
